import toast from 'react-hot-toast';

/**
 * Issue #86: 401 during a long data-entry session must not silently drop buffered
 * autosave values. Routes that run debounced saves can register a flusher here;
 * on 401 we flush synchronously (best-effort) before redirecting to /login, and
 * preserve the return path so the user lands back on the page they were editing.
 */
type Flusher = () => void | Promise<void>;
const pendingFlushers = new Set<Flusher>();

export function registerAutosaveFlusher(flusher: Flusher): () => void {
  pendingFlushers.add(flusher);
  return () => pendingFlushers.delete(flusher);
}

export async function flushAllPendingAutosaves(): Promise<void> {
  const flushers = Array.from(pendingFlushers);
  pendingFlushers.clear();
  // Fire in parallel; individual failures don't block the redirect.
  await Promise.allSettled(flushers.map((f) => Promise.resolve().then(f)));
}

// ApiClient falls back to "API error: 422" when the response carried no readable detail
// (pydantic validation errors arrive as a list). Say what it means instead.
const GENERIC_API_MESSAGE = /^API error: \d+/;

export function plainApiMessage(err: ApiError): string {
  if (err.message && !GENERIC_API_MESSAGE.test(err.message)) return err.message;
  if (err.status === 400 || err.status === 422) return "That value isn't accepted. Check it and try again.";
  if (err.status === 403) return 'You can only change your own entries from today.';
  if (err.status === 404) return 'This entry no longer exists. Reload.';
  if (err.status === 409) return 'This entry already exists. Reload to see it.';
  return "Couldn't save. Try again.";
}

export function saveFailureMessage(err: unknown, fallback: string): string {
  if (err instanceof DOMException && (err.name === 'TimeoutError' || err.name === 'AbortError')) {
    return 'Saving timed out. Check your connection and try again.';
  }
  if (err instanceof ApiError) return plainApiMessage(err);
  // fetch rejects with a bare TypeError ("Failed to fetch") when there is no connection.
  if (err instanceof TypeError) return "Couldn't save. Try again.";
  return err instanceof Error ? err.message : fallback;
}

// A timeout or lost connection says nothing about whether the server committed the create.
export function isUnconfirmedWrite(err: unknown): boolean {
  return !(err instanceof ApiError);
}

export function createFailureMessage(err: unknown, fallback: string): string {
  if (isUnconfirmedWrite(err)) return 'Not sure this saved. Check what is shown before entering it again.';
  if (err instanceof ApiError) return plainApiMessage(err);
  return err instanceof Error ? err.message : fallback;
}

export class ApiError extends Error {
  status: number;
  code?: string;
  details?: Record<string, string[]>;
  /** Raw FastAPI `detail` payload (string or object), preserved for callers
   * that need structured info such as per-field hard-limit rejections. */
  detail?: unknown;

  constructor(
    status: number,
    message: string,
    code?: string,
    details?: Record<string, string[]>,
    detail?: unknown
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
    this.detail = detail;
  }
}

/**
 * Extract the DB columns rejected by a hard-limit 400 ("Hard limit violations
 * detected"). The backend deliberately does NOT return the limit numbers; this
 * only surfaces WHICH fields failed so the UI can point at them.
 */
export function getHardLimitErrorFields(error: unknown): string[] {
  if (!(error instanceof ApiError) || error.status !== 400) return [];
  const detail = error.detail as { errors?: Array<{ field?: string }> } | undefined;
  if (!detail || !Array.isArray(detail.errors)) return [];
  return detail.errors
    .map((e) => e.field)
    .filter((f): f is string => typeof f === 'string' && f.length > 0);
}

/**
 * Columns a 422 names: FastAPI's body validation answers with a list of
 * `{ type, loc: ["body", "<column>"], msg }` (an integer column given 1.25 is `int_from_float`).
 * Empty when the response names no field, or is not a validation error.
 */
export function getValidationErrorFields(error: unknown): string[] {
  if (!(error instanceof ApiError) || error.status !== 422 || !Array.isArray(error.detail)) return [];
  const fields = (error.detail as Array<{ loc?: unknown }>).flatMap((e) => {
    const loc = e?.loc;
    if (!Array.isArray(loc) || loc[0] !== 'body') return [];
    const field = loc[loc.length - 1];
    return typeof field === 'string' && loc.length > 1 ? [field] : [];
  });
  return [...new Set(fields)];
}

/**
 * Columns a 400/422 refuses, with the server's own wording when it sends one. Covers the
 * object-shaped answer (`detail.errors[]`, e.g. "can't be empty" for a null on a required column,
 * or a hard-limit 400) and FastAPI's list-shaped validation errors. Empty when none is named.
 */
export function getRefusedFields(error: unknown): Array<{ field: string; message?: string }> {
  if (!(error instanceof ApiError) || (error.status !== 400 && error.status !== 422)) return [];
  const named = (error.detail as { errors?: unknown } | null | undefined)?.errors;
  if (Array.isArray(named)) {
    return named.flatMap((e: { field?: unknown; message?: unknown } | null) =>
      typeof e?.field === 'string' && e.field
        ? [{ field: e.field, message: typeof e.message === 'string' ? e.message : undefined }]
        : []
    );
  }
  return getValidationErrorFields(error).map((field) => ({ field }));
}

/**
 * Handle API errors with user-friendly toast notifications
 */
export function handleApiError(error: unknown): never {
  if (error instanceof ApiError) {
    // Validation errors - show per-field
    if (error.status === 422 && error.details) {
      const firstError = Object.values(error.details)[0]?.[0];
      toast.error(firstError || 'Validation error');
      throw error;
    }

    // Auth errors — preserve return path, best-effort flush buffered edits first.
    if (error.status === 401) {
      toast.error('Session expired. Please log in again.');
      const returnTo = window.location.pathname + window.location.search;
      // Fire-and-forget: kick off the flush, then redirect. Don't await (redirect
      // should still happen even if the user's token is fully dead and flushes fail).
      void flushAllPendingAutosaves();
      const redirectTo = returnTo && returnTo !== '/login'
        ? `/login?redirect=${encodeURIComponent(returnTo)}`
        : '/login';
      window.location.href = redirectTo;
      throw error;
    }

    // Forbidden
    if (error.status === 403) {
      toast.error('You do not have permission for this action.');
      throw error;
    }

    // Not found
    if (error.status === 404) {
      toast.error('The requested item was not found.');
      throw error;
    }

    // Server errors
    if (error.status >= 500) {
      toast.error('Server error. Please try again later.');
      throw error;
    }

    // Default
    toast.error(error.message || 'An error occurred');
    throw error;
  }

  // Network errors
  if (error instanceof TypeError && error.message.includes('fetch')) {
    toast.error('Network error. Please check your connection.');
    throw error;
  }

  // Unknown errors
  toast.error('An unexpected error occurred');
  throw error;
}

/**
 * Retry a function with exponential backoff
 */
export async function withRetry<T>(
  fn: () => Promise<T>,
  maxRetries: number = 3,
  delay: number = 1000
): Promise<T> {
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      if (attempt === maxRetries) {
        throw error;
      }
      // Only retry on network/server errors
      if (error instanceof ApiError && error.status < 500) {
        throw error;
      }
      await new Promise((r) => setTimeout(r, delay * Math.pow(2, attempt)));
    }
  }
  // This should never be reached, but TypeScript needs it
  throw new Error('Retry failed');
}

/**
 * Show a success toast
 */
export function showSuccess(message: string) {
  toast.success(message);
}

/**
 * Show an error toast
 */
export function showError(message: string) {
  toast.error(message);
}

/**
 * Show an info toast
 */
export function showInfo(message: string) {
  toast(message, { icon: 'ℹ️' });
}

/**
 * Show a loading toast that can be updated
 */
export function showLoading(message: string) {
  return toast.loading(message);
}

/**
 * Dismiss a specific toast or all toasts
 */
export function dismissToast(toastId?: string) {
  toast.dismiss(toastId);
}
